import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import AppShell from '../components/AppShell';

const ROLES = { store_admin:'Адміністратор', cashier:'Касир', super_admin:'Супер-адмін' };
const ROLE_COLORS = { super_admin:'#f5a623', store_admin:'#5fbf8f', cashier:'#6cb6ff' };


function StoreSettings({ store, onUpdate, storeId }) {
  const [form, setForm] = useState({ name:'', address:'', phone:'', legal_name:'', tax_id:'', receipt_footer:'', logo:'' });
  const [fiscal, setFiscal] = useState({ checkbox_enabled:false, checkbox_license_key:'', checkbox_cashier_login:'', checkbox_cashier_password:'', checkbox_cashier_pin:'' });
  const [logoErr, setLogoErr] = useState('');
  const [capEnabled, setCapEnabled] = useState(false);
  const [capProductId, setCapProductId] = useState('');
  const [bagEnabled, setBagEnabled] = useState(false);
  const [bagProductId, setBagProductId] = useState('');
  const [containers, setContainers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [previewSlug, setPreviewSlug] = useState('');

  useEffect(() => {
    if (store) {
      setForm({ name:store.name||'', address:store.address||'', phone:store.phone||'',
        legal_name:store.legal_name||'', tax_id:store.tax_id||'', receipt_footer:store.receipt_footer||'',
        logo:store.logo||'' });
      setCapEnabled(store.cap_enabled === true);
      setCapProductId(store.cap_product_id || '');
      setBagEnabled(store.bag_enabled === true);
      setBagProductId(store.bag_product_id || '');
      setFiscal({
        checkbox_enabled: store.checkbox_enabled === true,
        checkbox_license_key: store.checkbox_license_key || '',
        checkbox_cashier_login: store.checkbox_cashier_login || '',
        checkbox_cashier_password: store.checkbox_cashier_password || '',
        checkbox_cashier_pin: store.checkbox_cashier_pin || '',
      });
    }
  }, [store?.id]);

  useEffect(() => {
    api.get(`/containers/stores/${storeId}`)
      .then(r => setContainers(r.data))
      .catch(() => {});
  }, [storeId]);

  const TRANSLIT = {
    'а':'a','б':'b','в':'v','г':'g','ґ':'g','д':'d','е':'e','є':'ye',
    'ж':'zh','з':'z','и':'y','і':'i','ї':'yi','й':'y','к':'k','л':'l',
    'м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
    'ф':'f','х':'kh','ц':'ts','ч':'ch','ш':'sh','щ':'shch','ь':'',
    'ю':'yu','я':'ya','ъ':'','ы':'y','э':'e','ё':'yo',
  };

  function makeSlugPreview(name) {
    let result = '';
    for (const char of name.toLowerCase()) {
      result += TRANSLIT[char] !== undefined ? TRANSLIT[char] : char;
    }
    return result.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 100) || 'store';
  }

  function onLogoFile(e) {
    setLogoErr('');
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { setLogoErr('Оберіть зображення'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Зменшуємо до ширини ~360px (достатньо для друку на 58мм) і пакуємо в base64
        const maxW = 360;
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h); // білий фон (термопринтер не друкує прозоре)
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/png');
        if (dataUrl.length > 700000) { setLogoErr('Замале стиснення — оберіть простіший логотип'); return; }
        setForm(f => ({ ...f, logo: dataUrl }));
      };
      img.onerror = () => setLogoErr('Не вдалося прочитати зображення');
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await api.patch(`/stores/${storeId}`, {
        ...form,
        cap_enabled: capEnabled,
        cap_product_id: capEnabled && capProductId ? capProductId : null,
        bag_enabled: bagEnabled,
        bag_product_id: bagEnabled && bagProductId ? bagProductId : null,
        ...fiscal,
      });
      onUpdate(res.data);
      setSuccess('Збережено!');
      setTimeout(() => setSuccess(''), 3000);
    } catch(err) {
      setError(err.response?.data?.detail || 'Помилка');
    } finally { setSaving(false); }
  }

  async function deactivateStore() {
    if (!window.confirm(`Деактивувати магазин "${store.name}"?`)) return;
    try {
      await api.patch(`/stores/${storeId}`, { is_active: false });
      alert('Магазин деактивовано');
    } catch(err) { alert(err.response?.data?.detail || 'Помилка'); }
  }

  function Toggle({ enabled, onChange }) {
    return (
      <div onClick={() => onChange(!enabled)} style={{cursor:'pointer'}}>
        <div style={{...settingsStyles.toggleTrack, ...(enabled ? settingsStyles.toggleTrackOn : {})}}>
          <div style={{...settingsStyles.toggleThumb, ...(enabled ? settingsStyles.toggleThumbOn : {})}}/>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 style={settingsStyles.title}>⚙️ Налаштування магазину</h3>
      <form onSubmit={handleSubmit} style={settingsStyles.form}>
        <div style={settingsStyles.fieldGroup}>
          <label style={settingsStyles.label}>Назва магазину</label>
          <input style={settingsStyles.input} value={form.name} required
            onChange={e => { setForm(f=>({...f,name:e.target.value})); setPreviewSlug(makeSlugPreview(e.target.value)); }} />
          {previewSlug && form.name !== store?.name ? (
            <div style={settingsStyles.slugPreview}>🔗 Новий slug: <strong>{previewSlug}</strong><span style={settingsStyles.slugHint}> (авто)</span></div>
          ) : (
            <div style={settingsStyles.slugCurrent}>🔗 Slug: <strong>{store?.slug}</strong></div>
          )}
        </div>
        <div style={settingsStyles.fieldGroup}>
          <label style={settingsStyles.label}>Адреса</label>
          <input style={settingsStyles.input} value={form.address}
            onChange={e => setForm(f=>({...f,address:e.target.value}))} placeholder="вул. Головна 1" />
        </div>
        <div style={settingsStyles.fieldGroup}>
          <label style={settingsStyles.label}>Телефон</label>
          <input style={settingsStyles.input} value={form.phone}
            onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="+380501234567" />
        </div>

        {/* Реквізити для чека */}
        <div style={settingsStyles.autoSection}>
          <h4 style={settingsStyles.autoTitle}>🧾 Реквізити для чека</h4>
          <p style={settingsStyles.autoHint}>Показуються у чеку цієї точки (можна редагувати окремо для кожного магазину)</p>
          <div style={settingsStyles.fieldGroup}>
            <label style={settingsStyles.label}>Логотип (для етикеток і чека)</label>
            <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div style={{width:120,height:64,border:'1px dashed #b89968',borderRadius:8,display:'flex',
                alignItems:'center',justifyContent:'center',background:'#fff',overflow:'hidden'}}>
                {form.logo
                  ? <img src={form.logo} alt="logo" style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain'}} />
                  : <span style={{fontSize:11,color:'#9a8a6a'}}>немає</span>}
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                <label style={{...settingsStyles.input,cursor:'pointer',padding:'8px 12px',display:'inline-block',width:'auto'}}>
                  📁 Завантажити
                  <input type="file" accept="image/*" onChange={onLogoFile} style={{display:'none'}} />
                </label>
                {form.logo && (
                  <button type="button" onClick={() => setForm(f=>({...f,logo:''}))}
                    style={{background:'none',border:'none',color:'#c0392b',cursor:'pointer',fontSize:12,textAlign:'left',padding:0}}>
                    ✕ Прибрати логотип
                  </button>
                )}
              </div>
            </div>
            {logoErr && <div style={{color:'#c0392b',fontSize:12,marginTop:4}}>{logoErr}</div>}
            <p style={settingsStyles.autoHint}>Краще квадратний/горизонтальний PNG. Якщо порожньо — на етикетці буде стандартний значок Draft Beer.</p>
          </div>
          <div style={settingsStyles.fieldGroup}>
            <label style={settingsStyles.label}>ФОП / Назва (для чека)</label>
            <input style={settingsStyles.input} value={form.legal_name}
              onChange={e => setForm(f=>({...f,legal_name:e.target.value}))} placeholder="ФОП Чумаченко А.Ю." />
          </div>
          <div style={settingsStyles.fieldGroup}>
            <label style={settingsStyles.label}>ЄДРПОУ / ІПН</label>
            <input style={settingsStyles.input} value={form.tax_id}
              onChange={e => setForm(f=>({...f,tax_id:e.target.value}))} placeholder="3455211362" />
          </div>
          <div style={settingsStyles.fieldGroup}>
            <label style={settingsStyles.label}>Текст унизу чека</label>
            <input style={settingsStyles.input} value={form.receipt_footer}
              onChange={e => setForm(f=>({...f,receipt_footer:e.target.value}))} placeholder="Дякуємо за покупку! 🍺" />
          </div>
        </div>

        {/* Автоматичні позиції */}
        <div style={settingsStyles.autoSection}>
          <h4 style={settingsStyles.autoTitle}>🔩 Автоматичні позиції</h4>
          <p style={settingsStyles.autoHint}>Автоматично додаються в кожен чек при продажу відповідних товарів</p>

          {containers.length === 0 && (
            <div style={settingsStyles.warningBox}>
              ⚠️ Спочатку додайте тару в Каталог → 🍶 Тара
            </div>
          )}

          {/* Кришка */}
          <div style={settingsStyles.toggleRow}>
            <div style={settingsStyles.toggleInfo}>
              <div style={settingsStyles.toggleLabel}>🔩 Кришка для розливних товарів</div>
              <div style={settingsStyles.toggleDesc}>Додається до кожного літрового товару</div>
            </div>
            <Toggle enabled={capEnabled} onChange={setCapEnabled} />
          </div>
          {capEnabled && (
            <div style={settingsStyles.priceRow}>
              <label style={settingsStyles.label}>Оберіть товар кришки</label>
              <select style={settingsStyles.input} value={capProductId}
                onChange={e => setCapProductId(e.target.value)} required={capEnabled}>
                <option value="">Оберіть зі списку тари...</option>
                {containers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {parseFloat(c.price).toFixed(2)} ₴ (залишок: {parseFloat(c.stock_quantity).toFixed(0)} шт)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Пакет */}
          <div style={{...settingsStyles.toggleRow, marginTop:'14px'}}>
            <div style={settingsStyles.toggleInfo}>
              <div style={settingsStyles.toggleLabel}>🛍 Пакет для вагових товарів</div>
              <div style={settingsStyles.toggleDesc}>Додається до кожного вагового товару</div>
            </div>
            <Toggle enabled={bagEnabled} onChange={setBagEnabled} />
          </div>
          {bagEnabled && (
            <div style={settingsStyles.priceRow}>
              <label style={settingsStyles.label}>Оберіть товар пакету</label>
              <select style={settingsStyles.input} value={bagProductId}
                onChange={e => setBagProductId(e.target.value)} required={bagEnabled}>
                <option value="">Оберіть зі списку тари...</option>
                {containers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {parseFloat(c.price).toFixed(2)} ₴ (залишок: {parseFloat(c.stock_quantity).toFixed(0)} шт)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Каса Checkbox (фіскалізація цієї точки) */}
        <div style={settingsStyles.autoSection}>
          <h4 style={settingsStyles.autoTitle}>🧾 Каса Checkbox (фіскалізація цієї точки)</h4>
          <p style={settingsStyles.autoHint}>Своя каса та касир для цього магазину. Дані беруться з кабінету Checkbox.</p>
          <label style={{...settingsStyles.toggle, marginBottom:12}}>
            <input type="checkbox" checked={fiscal.checkbox_enabled}
              onChange={e => setFiscal(f => ({...f, checkbox_enabled: e.target.checked}))} />
            <span style={settingsStyles.toggleLabel}>Увімкнути фіскалізацію в цій точці</span>
          </label>
          <div style={settingsStyles.fieldGroup}>
            <label style={settingsStyles.label}>License Key каси</label>
            <input style={settingsStyles.input} value={fiscal.checkbox_license_key}
              onChange={e => setFiscal(f=>({...f,checkbox_license_key:e.target.value}))}
              placeholder="ключ ліцензії каси з Checkbox" />
          </div>
          <div style={settingsStyles.fieldGroup}>
            <label style={settingsStyles.label}>Логін касира</label>
            <input style={settingsStyles.input} value={fiscal.checkbox_cashier_login}
              onChange={e => setFiscal(f=>({...f,checkbox_cashier_login:e.target.value}))}
              placeholder="напр. Касир 1" />
          </div>
          <div style={settingsStyles.fieldGroup}>
            <label style={settingsStyles.label}>Пароль касира</label>
            <input style={settingsStyles.input} type="password" autoComplete="new-password"
              value={fiscal.checkbox_cashier_password}
              onChange={e => setFiscal(f=>({...f,checkbox_cashier_password:e.target.value}))} />
          </div>
          <div style={settingsStyles.fieldGroup}>
            <label style={settingsStyles.label}>PIN касира (опц.)</label>
            <input style={settingsStyles.input} type="password" autoComplete="new-password"
              value={fiscal.checkbox_cashier_pin}
              onChange={e => setFiscal(f=>({...f,checkbox_cashier_pin:e.target.value}))} />
          </div>
        </div>

        {error && <p style={settingsStyles.error}>{error}</p>}
        {success && <p style={settingsStyles.success}>✓ {success}</p>}
        <button type="submit" style={settingsStyles.saveBtn} disabled={saving}>
          {saving ? 'Збереження...' : '💾 Зберегти всі налаштування'}
        </button>
      </form>

      <div style={settingsStyles.dangerZone}>
        <h4 style={settingsStyles.dangerTitle}>⚠️ Небезпечна зона</h4>
        <div style={settingsStyles.dangerRow}>
          <div>
            <div style={settingsStyles.dangerLabel}>Деактивувати магазин</div>
            <div style={settingsStyles.dangerDesc}>Магазин зникне зі списку. Всі дані збережуться.</div>
          </div>
          <button style={settingsStyles.dangerBtn} onClick={deactivateStore}>Деактивувати</button>
        </div>
      </div>
    </div>
  );
}


const settingsStyles = {
  title: { color:'#f5a623', marginBottom:'20px', marginTop:0 },
  form: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'24px', maxWidth:'500px', marginBottom:'24px' },
  fieldGroup: { marginBottom:'16px' },
  label: { display:'block', color:'#9a8a6e', fontSize:'11px', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' },
  input: { width:'100%', padding:'11px 14px', borderRadius:'8px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'14px', boxSizing:'border-box' },
  slugPreview: { marginTop:'6px', color:'#6cb6ff', fontSize:'12px' },
  slugCurrent: { marginTop:'6px', color:'#9a8a6e', fontSize:'12px' },
  slugHint: { color:'#9a8a6e' },
  autoSection: { background:'#1c1812', borderRadius:'10px', padding:'16px', marginBottom:'16px', border:'1px solid #38301f' },
  autoTitle: { color:'#f5a623', margin:'0 0 6px', fontSize:'14px' },
  autoHint: { color:'#9a8a6e', fontSize:'12px', margin:'0 0 14px' },
  toggleRow: { display:'flex', justifyContent:'space-between', alignItems:'center' },
  toggleInfo: { flex:1 },
  toggleLabel: { fontWeight:'bold', fontSize:'14px', marginBottom:'2px' },
  toggleDesc: { color:'#9a8a6e', fontSize:'12px' },
  toggleTrack: { width:'48px', height:'26px', background:'#38301f', borderRadius:'13px', position:'relative', transition:'background 0.2s', flexShrink:0 },
  toggleTrackOn: { background:'#2d6a4f' },
  toggleThumb: { position:'absolute', top:'3px', left:'3px', width:'20px', height:'20px', background:'#9a8a6e', borderRadius:'50%', transition:'left 0.2s, background 0.2s' },
  toggleThumbOn: { left:'25px', background:'#5fbf8f' },
  priceRow: { marginTop:'10px', marginLeft:'0' },
  error: { color:'#ff6b6b', fontSize:'13px', marginBottom:'12px' },
  success: { color:'#5fbf8f', fontSize:'13px', marginBottom:'12px' },
  saveBtn: { padding:'11px 28px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', fontSize:'14px' },
  dangerZone: { background:'#1a0808', border:'1px solid rgba(255,107,107,.18)', borderRadius:'12px', padding:'20px', maxWidth:'500px' },
  dangerTitle: { color:'#ff6b6b', margin:'0 0 14px', fontSize:'14px' },
  dangerRow: { display:'flex', justifyContent:'space-between', alignItems:'center', gap:'16px' },
  dangerLabel: { fontWeight:'bold', fontSize:'14px', marginBottom:'4px' },
  dangerDesc: { color:'#9a8a6e', fontSize:'13px' },
  dangerBtn: { padding:'9px 18px', background:'transparent', color:'#ff6b6b', border:'1px solid rgba(255,107,107,.18)', borderRadius:'8px', cursor:'pointer', fontSize:'13px', whiteSpace:'nowrap', flexShrink:0 },
};


export default function StorePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStoreAdmin, isSuperAdmin, hasAccessToStore, myRoleInStore, user } = useAuth();
  const [store, setStore] = useState(null);
  const [activeShift, setActiveShift] = useState(undefined);
  const [myShift, setMyShift] = useState(undefined);
  const [shiftHistory, setShiftHistory] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState(null);
  const [staffForm, setStaffForm] = useState({ email:'', full_name:'', password:'', role:'cashier' });
  const [staffError, setStaffError] = useState('');
  const [staffSuccess, setStaffSuccess] = useState('');
  const [shiftError, setShiftError] = useState('');
  const [prroShift, setPrroShift] = useState(null);  // реальна зміна з ПРРО (десктоп-каса)

  const canManage = isStoreAdmin(id);
  const isCashier = !canManage;
  const isDesktop = typeof window !== 'undefined' && !!window.hardware;
  const role = myRoleInStore ? myRoleInStore(id) : null;

  // Визначаємо вкладки залежно від ролі
  const tabs = [
    { key:'shift', label:'🕐 Зміна', roles:['cashier','store_admin','super_admin'] },
    { key:'pos', label:'💰 Каса', roles:['cashier','store_admin','super_admin'] },
    { key:'catalog', label:'📦 Каталог', roles:['store_admin','super_admin'] },
    { key:'inventory', label:'🛒 Склад', roles:['store_admin','super_admin'] },
    { key:'analytics', label:'📊 Аналітика', roles:['store_admin','super_admin'] },
    { key:'staff', label:'👥 Персонал', roles:['store_admin','super_admin'] },
    { key:'promotions', label:'🎯 Акції', roles:['store_admin','super_admin'] },
    { key:'settings', label:'⚙️ Налаштування', roles:['super_admin'] },
  ].filter(t => {
    const r = isSuperAdmin ? 'super_admin' : (canManage ? 'store_admin' : 'cashier');
    return t.roles.includes(r);
  });

  useEffect(() => {
    if (!hasAccessToStore(id)) { navigate('/dashboard'); return; }
    api.get('/stores/').then(r => setStore(r.data.find(s => s.id === id)));
    loadShiftData();
    if (canManage) api.get(`/stores/${id}/staff`).then(r => setStaff(r.data));
    // Дефолтна вкладка
    setTab(isCashier ? 'shift' : 'shift');
    // Перечитати статус зміни при поверненні фокусу (напр. після вводу пароля ПРРО в Налаштуваннях)
    const onFocus = () => { if (document.visibilityState === 'visible') loadShiftData(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [id]);

  async function loadShiftData() {
    let [storeShift, myShiftRes] = await Promise.all([
      api.get(`/shifts/store/${id}/active`).then(r => r.data).catch(() => null),
      api.get('/shifts/my').then(r => r.data).catch(() => null),
    ]);
    // Десктоп-каса: реальна зміна = ПРРО iNNOVATE. Якщо ПРРО на звʼязку — тихо синхронізуємо
    // CRM-зміну (внутрішній запис; ДРУГУ фіскальну зміну в ПРРО це НЕ створює), щоб касир
    // не відкривав зміну вручну і бачив правильний статус.
    if (isDesktop && window.hardware.prro && window.hardware.prro.status) {
      try {
        const st = await window.hardware.prro.status();
        if (st && st.ok) {
          setPrroShift(st);
          if (!myShiftRes && !storeShift) {
            const opened = (await api.post(`/shifts/open/${id}`).then(r => r.data).catch(() => null));
            if (opened) { myShiftRes = opened; storeShift = opened; }
          }
        }
      } catch (e) {}
    }
    setActiveShift(storeShift);
    setMyShift(myShiftRes);
    if (canManage) {
      api.get(`/shifts/store/${id}/history`).then(r => setShiftHistory(r.data)).catch(() => {});
    }
  }

  async function openShift() {
    setShiftError('');
    try {
      await api.post(`/shifts/open/${id}`);
      await loadShiftData();
    } catch(err) { setShiftError(err.response?.data?.detail || 'Помилка'); }
  }

  async function closeShift() {
    setShiftError('');
    try {
      // Десктоп + ПРРО: закриття зміни = Z-звіт у ПРРО (офіційне закриття + друк Z).
      if (isDesktop && window.hardware.prro && window.hardware.prro.zreport) {
        let prroOpen = false;
        try { const st = await window.hardware.prro.status(); prroOpen = !!(st && st.ok && st.shiftOpen); } catch (e) {}
        if (prroOpen) {
          const z = await window.hardware.prro.zreport(user?.full_name || user?.email || 'Касир');
          if (!z || !z.ok) {
            setShiftError('Не вдалось закрити зміну в ПРРО (Z-звіт): ' + ((z && z.error) || 'помилка') +
              '. CRM-зміну не закрито, щоб не розійтись із ПРРО.');
            return;  // не закриваємо CRM-зміну, якщо Z не пройшов
          }
          // друк Z-звіту, якщо ПРРО повернув текст
          if (z.data && z.data.text && window.hardware.printReceipt) {
            try {
              window.hardware.printReceipt(
                '<html><head><meta charset="utf-8"><style>@page{margin:0}body{width:48mm;margin:0;padding:1mm 1.5mm}'
                + 'pre{font-family:"Courier New",monospace;color:#000;font-size:9pt;line-height:1.2;white-space:pre-wrap;word-break:break-word;margin:0}'
                + '</style></head><body><pre>'
                + String(z.data.text).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]))
                + '</pre></body></html>'
              ).catch(() => {});
            } catch (e) {}
          }
        }
      }
      await api.post(`/shifts/close/${myShift.id}`);
      await loadShiftData();
    } catch(err) { setShiftError(err.response?.data?.detail || 'Помилка'); }
  }

  async function forceClose(shiftId) {
    await api.post(`/shifts/force-close/${shiftId}`);
    await loadShiftData();
  }

  async function addStaff(e) {
    e.preventDefault(); setStaffError(''); setStaffSuccess('');
    try {
      await api.post(`/stores/${id}/staff`, staffForm);
      const r = await api.get(`/stores/${id}/staff`);
      setStaff(r.data);
      setStaffForm({ email:'', full_name:'', password:'', role:'cashier' });
      setStaffSuccess('Співробітника додано!');
    } catch(err) { setStaffError(err.response?.data?.detail || 'Помилка'); }
  }

  function formatTime(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('uk-UA', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' });
  }

  function duration(open, close) {
    const ms = (close ? new Date(close) : new Date()) - new Date(open);
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}г ${m}хв`;
  }

  const myShiftIsHere = myShift && myShift.store_id === id;
  const myShiftElsewhere = myShift && myShift.store_id !== id;
  const someoneElseWorking = activeShift && activeShift.cashier_id !== user?.id;
  const canOpenPOS = myShiftIsHere || (canManage && activeShift);

  if (!store) return <div className="hint">Завантаження…</div>;

  return (
    <AppShell
      title={store.name}
      subtitle={store.address}
      onBack={() => navigate('/dashboard')}
      right={
        <span className={`badge ${activeShift ? 'badge-green' : ''} hide-mobile`}>
          {activeShift ? '🟢 Зміна відкрита' : '⚫ Зміна закрита'}
        </span>
      }
    >
      {/* Вкладки */}
      <div className="tabbar">
        {tabs.map(t => (
          <button key={t.key}
            className={`tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 820, marginTop: 6 }}>

        {/* ── ЗМІНА ── */}
        {tab === 'shift' && (
          <div style={styles.section}>
            {shiftError && <p style={styles.error}>{shiftError}</p>}

            {/* Касир */}
            {isCashier && (
              <div>
                <h3 style={styles.sectionTitle}>Моя зміна</h3>
                {myShiftIsHere ? (
                  <div style={styles.shiftCard}>
                    <div style={styles.shiftStatusOpen}>🟢 Зміна відкрита</div>
                    <p style={styles.shiftMeta}>Початок: {formatTime(myShift.opened_at)} · {duration(myShift.opened_at, null)}</p>
                    <div style={styles.shiftBtns}>
                      <button style={styles.posBtn} onClick={() => navigate(`/pos/${id}`)}>
                        💰 Відкрити касу
                      </button>
                      <button style={styles.closeShiftBtn} onClick={closeShift}>
                        Закрити зміну
                      </button>
                    </div>
                  </div>
                ) : myShiftElsewhere ? (
                  <div style={styles.shiftCard}>
                    <div style={styles.shiftStatusWarn}>⚠️ У вас відкрита зміна в іншому магазині</div>
                    <p style={styles.shiftMeta}>Закрийте ту зміну щоб почати тут</p>
                    <button style={styles.closeShiftBtn} onClick={closeShift}>Закрити ту зміну</button>
                  </div>
                ) : someoneElseWorking ? (
                  <div style={styles.shiftCard}>
                    <div style={styles.shiftStatusClosed}>🔴 Магазин зайнятий — працює інший касир</div>
                  </div>
                ) : (
                  <div style={styles.shiftCard}>
                    <div style={styles.shiftStatusClosed}>⚫ Зміна не відкрита</div>
                    <p style={styles.shiftMeta}>Відкрийте зміну щоб почати роботу</p>
                    <button style={styles.openShiftBtn} onClick={openShift}>🟢 Відкрити зміну</button>
                  </div>
                )}
              </div>
            )}

            {/* Адмін */}
            {canManage && (
              <div>
                <h3 style={styles.sectionTitle}>Поточна зміна</h3>
                {activeShift ? (
                  <div style={styles.shiftCard}>
                    <div style={styles.shiftStatusOpen}>🟢 Зміна відкрита</div>
                    <p style={styles.shiftMeta}>
                      Початок: {formatTime(activeShift.opened_at)} · {duration(activeShift.opened_at, null)}
                    </p>
                    <div style={styles.shiftBtns}>
                      <button style={styles.posBtn} onClick={() => navigate(`/pos/${id}`)}>
                        💰 Відкрити касу
                      </button>
                      <button style={styles.closeShiftBtn} onClick={() => forceClose(activeShift.id)}>
                        Примусово закрити
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.shiftCard}>
                    <div style={styles.shiftStatusClosed}>⚫ Немає активної зміни</div>
                  </div>
                )}

                {shiftHistory.length > 0 && (
                  <div style={{marginTop:'24px'}}>
                    <h3 style={styles.sectionTitle}>Історія змін</h3>
                    <div style={styles.historyList}>
                      {shiftHistory.map(s => (
                        <div key={s.id} style={styles.historyRow}>
                          <span style={s.status==='open' ? styles.dotOpen : styles.dotClosed}>●</span>
                          <div style={{flex:1}}>
                            <div style={styles.historyTime}>
                              {formatTime(s.opened_at)} → {s.closed_at ? formatTime(s.closed_at) : 'зараз'}
                            </div>
                            <div style={styles.historyDur}>{duration(s.opened_at, s.closed_at)}</div>
                          </div>
                          <span style={s.status==='open' ? styles.badgeOpen : styles.badgeClosed}>
                            {s.status==='open' ? 'Відкрита' : 'Закрита'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── КАСА ── */}
        {tab === 'pos' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>💰 POS Каса</h3>
            {canOpenPOS ? (
              <div style={styles.posCard}>
                <div style={styles.posIcon}>💰</div>
                <h3 style={styles.posTitle}>Зміна відкрита — можна працювати</h3>
                <p style={styles.posDesc}>Продаж товарів, бонусна система, чеки</p>
                <button style={styles.openPosBtn} onClick={() => navigate(`/pos/${id}`)}>
                  Відкрити касу →
                </button>
              </div>
            ) : (
              <div style={styles.posCard}>
                <div style={styles.posIcon}>🔒</div>
                <h3 style={styles.posTitle}>Спочатку відкрийте зміну</h3>
                <p style={styles.posDesc}>Перейдіть на вкладку "Зміна" і відкрийте зміну</p>
                <button style={styles.goShiftBtn} onClick={() => setTab('shift')}>
                  Перейти до зміни →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── КАТАЛОГ ── */}
        {tab === 'catalog' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📦 Каталог товарів</h3>
            <div style={styles.menuGrid}>
              <div style={styles.menuCard} onClick={() => navigate(`/catalog/${id}`)}>
                <div style={styles.menuIcon}>📦</div>
                <div style={styles.menuTitle}>Товари та категорії</div>
                <div style={styles.menuDesc}>Додавання, редагування, ціни, категорії з іконками</div>
              </div>
            </div>
          </div>
        )}

        {/* ── СКЛАД ── */}
        {tab === 'inventory' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🛒 Складський облік</h3>
            <div style={styles.menuGrid}>
              <div style={styles.menuCard} onClick={() => navigate(`/inventory/${id}`)}>
                <div style={styles.menuIcon}>🛒</div>
                <div style={styles.menuTitle}>Прихід та списання</div>
                <div style={styles.menuDesc}>Поповнення залишків, списання, історія руху товарів</div>
              </div>
            </div>
          </div>
        )}

        {/* ── АНАЛІТИКА ── */}
        {tab === 'analytics' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📊 Аналітика</h3>
            <div style={styles.menuGrid}>
              <div style={styles.menuCard} onClick={() => navigate(`/analytics/${id}`)}>
                <div style={styles.menuIcon}>📊</div>
                <div style={styles.menuTitle}>Залишки та рух товарів</div>
                <div style={styles.menuDesc}>Що є, що закінчується, топ товарів</div>
              </div>
            </div>
          </div>
        )}

        {/* ── ПЕРСОНАЛ ── */}
        {tab === 'staff' && canManage && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Додати співробітника</h3>
            <form onSubmit={addStaff} style={styles.staffForm}>
              <input style={styles.input} placeholder="Email" type="email"
                value={staffForm.email} onChange={e => setStaffForm({...staffForm, email:e.target.value})} required />
              <input style={styles.input} placeholder="Повне ім'я"
                value={staffForm.full_name} onChange={e => setStaffForm({...staffForm, full_name:e.target.value})} required />
              <input style={styles.input} placeholder="Пароль" type="password"
                value={staffForm.password} onChange={e => setStaffForm({...staffForm, password:e.target.value})} required />
              <select style={styles.input} value={staffForm.role}
                onChange={e => setStaffForm({...staffForm, role:e.target.value})}>
                <option value="cashier">Касир</option>
                {isSuperAdmin && <option value="store_admin">Адміністратор</option>}
              </select>
              <button style={styles.addStaffBtn} type="submit">Додати</button>
            </form>
            {staffError && <p style={styles.error}>{staffError}</p>}
            {staffSuccess && <p style={styles.success}>{staffSuccess}</p>}

            <h3 style={{...styles.sectionTitle, marginTop:'24px'}}>Співробітники ({staff.length})</h3>
            {staff.length === 0 ? <p style={styles.hint}>Поки немає співробітників</p> : (
              <div style={styles.staffList}>
                {staff.map(s => (
                  <div key={s.id} style={styles.staffCard}>
                    <div style={styles.staffAvatar}>{s.full_name?.[0]?.toUpperCase() || '?'}</div>
                    <div style={{flex:1}}>
                      <div style={styles.staffName}>{s.full_name}</div>
                      <div style={styles.staffEmail}>{s.email}</div>
                    </div>
                    <span style={{...styles.staffRole, color: ROLE_COLORS[s.role]}}>
                      {ROLES[s.role] || s.role}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── АКЦІЇ ── */}
        {tab === 'promotions' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🎯 Акції та знижки</h3>
            <div style={styles.menuGrid}>
              <div style={styles.menuCard} onClick={() => navigate(`/promotions/${id}`)}>
                <div style={styles.menuIcon}>🎯</div>
                <div style={styles.menuTitle}>Управління акціями</div>
                <div style={styles.menuDesc}>Створення знижок: 1+1, відсоткові знижки, фіксовані знижки</div>
              </div>
            </div>
          </div>
        )}

        {/* ── НАЛАШТУВАННЯ ── */}
        {tab === 'settings' && isSuperAdmin && (
          <StoreSettings store={store} onUpdate={setStore} storeId={id} />
        )}

      </div>
    </AppShell>
  );
}

const styles = {
  container: { minHeight:'100vh', background:'#14110c', color:'#fff' },
  loading: { color:'#fff', padding:'40px', textAlign:'center' },

  // Шапка
  header: { background:'#211c14', padding:'14px 28px', display:'flex', alignItems:'center', gap:'16px', borderBottom:'1px solid #38301f', flexWrap:'wrap' },
  back: { background:'transparent', color:'#f5a623', border:'none', fontSize:'16px', cursor:'pointer', flexShrink:0 },
  storeInfo: { flex:1 },
  storeName: { color:'#f5a623', margin:0, fontSize:'20px' },
  storeAddr: { color:'#9a8a6e', fontSize:'13px' },
  headerRight: { display:'flex', alignItems:'center', gap:'10px' },
  shiftOpen: { background:'#2d6a4f', color:'#5fbf8f', padding:'5px 12px', borderRadius:'20px', fontSize:'13px' },
  shiftClosed: { background:'#38301f', color:'#9a8a6e', padding:'5px 12px', borderRadius:'20px', fontSize:'13px' },
  rolePill: { fontSize:'12px', fontWeight:'bold' },

  // Вкладки
  tabBar: { background:'#211c14', borderBottom:'1px solid #38301f', padding:'0 28px', display:'flex', gap:'0', overflowX:'auto' },
  tab: { padding:'14px 20px', background:'transparent', color:'#9a8a6e', border:'none', borderBottom:'2px solid transparent', cursor:'pointer', fontSize:'14px', whiteSpace:'nowrap' },
  tabActive: { padding:'14px 20px', background:'transparent', color:'#f5a623', border:'none', borderBottom:'2px solid #f5a623', cursor:'pointer', fontSize:'14px', fontWeight:'bold', whiteSpace:'nowrap' },

  // Контент
  main: { maxWidth:'900px', margin:'0 auto', padding:'28px 24px' },
  section: {},
  sectionTitle: { color:'#f5a623', marginBottom:'16px', marginTop:0, fontSize:'16px' },

  // Зміна
  shiftCard: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'20px', maxWidth:'480px', marginBottom:'16px' },
  shiftStatusOpen: { fontSize:'17px', fontWeight:'bold', color:'#5fbf8f', marginBottom:'8px' },
  shiftStatusWarn: { fontSize:'17px', fontWeight:'bold', color:'#f5a623', marginBottom:'8px' },
  shiftStatusClosed: { fontSize:'17px', fontWeight:'bold', color:'#9a8a6e', marginBottom:'8px' },
  shiftMeta: { color:'#9a8a6e', fontSize:'13px', margin:'0 0 16px' },
  shiftBtns: { display:'flex', gap:'10px', flexWrap:'wrap' },
  openShiftBtn: { padding:'11px 24px', background:'#2d6a4f', color:'#5fbf8f', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', fontSize:'14px' },
  closeShiftBtn: { padding:'11px 20px', background:'rgba(255,107,107,.18)', color:'#ff6b6b', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px' },
  posBtn: { padding:'11px 24px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', fontSize:'14px' },

  // Каса
  posCard: { background:'#211c14', border:'1px solid #38301f', borderRadius:'16px', padding:'32px', maxWidth:'400px', textAlign:'center' },
  posIcon: { fontSize:'48px', marginBottom:'12px' },
  posTitle: { color:'#fff', fontSize:'18px', fontWeight:'bold', marginBottom:'8px', margin:'0 0 8px' },
  posDesc: { color:'#9a8a6e', fontSize:'14px', marginBottom:'24px' },
  openPosBtn: { padding:'13px 32px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'10px', fontWeight:'bold', cursor:'pointer', fontSize:'15px' },
  goShiftBtn: { padding:'13px 32px', background:'#38301f', color:'#9a8a6e', border:'none', borderRadius:'10px', cursor:'pointer', fontSize:'15px' },

  // Меню
  menuGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'16px' },
  menuCard: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'24px', cursor:'pointer' },
  menuIcon: { fontSize:'36px', marginBottom:'12px' },
  menuTitle: { fontWeight:'bold', marginBottom:'8px', fontSize:'16px' },
  menuDesc: { color:'#9a8a6e', fontSize:'13px', lineHeight:'1.5' },

  // Персонал
  staffForm: { display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'8px' },
  input: { flex:1, minWidth:'150px', padding:'10px 14px', borderRadius:'8px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'14px' },
  addStaffBtn: { padding:'10px 20px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer' },
  staffList: { display:'flex', flexDirection:'column', gap:'8px' },
  staffCard: { background:'#211c14', borderRadius:'10px', padding:'14px 18px', border:'1px solid #38301f', display:'flex', alignItems:'center', gap:'14px' },
  staffAvatar: { width:'38px', height:'38px', borderRadius:'50%', background:'#1c1812', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', fontWeight:'bold', color:'#f5a623', flexShrink:0 },
  staffName: { fontWeight:'bold', marginBottom:'2px', fontSize:'14px' },
  staffEmail: { color:'#9a8a6e', fontSize:'12px' },
  staffRole: { background:'#38301f', padding:'3px 10px', borderRadius:'20px', fontSize:'12px' },

  // Налаштування
  settingsCard: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'20px', maxWidth:'480px' },
  settingsRow: { display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid #38301f' },
  settingsLabel: { color:'#9a8a6e', fontSize:'14px' },
  settingsValue: { color:'#fff', fontSize:'14px' },
  settingsHint: { color:'#9a8a6e', fontSize:'13px', marginTop:'16px', marginBottom:0 },
  toggleRow: { display:'flex', justifyContent:'space-between', alignItems:'center' },
  toggleInfo: { flex:1 },
  toggleLabel: { fontWeight:'bold', fontSize:'14px', marginBottom:'2px' },
  toggleDesc: { color:'#9a8a6e', fontSize:'12px' },
  toggle: { cursor:'pointer', flexShrink:0, marginLeft:'16px' },
  toggleTrack: { width:'44px', height:'24px', background:'#38301f', borderRadius:'12px', position:'relative', transition:'background 0.2s' },
  toggleTrackOn: { background:'#2d6a4f' },
  toggleThumb: { position:'absolute', top:'2px', left:'2px', width:'20px', height:'20px', background:'#9a8a6e', borderRadius:'50%', transition:'left 0.2s, background 0.2s' },
  toggleThumbOn: { left:'22px', background:'#5fbf8f' },
  priceRow: { marginTop:'10px', marginLeft:'0' },

  // Спільне
  error: { color:'#ff6b6b', fontSize:'13px', marginTop:'8px' },
  success: { color:'#5fbf8f', fontSize:'13px', marginTop:'8px' },
  hint: { color:'#9a8a6e', textAlign:'center', padding:'20px' },
  historyList: { display:'flex', flexDirection:'column', gap:'8px' },
  historyRow: { background:'#211c14', border:'1px solid #38301f', borderRadius:'8px', padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px' },
  dotOpen: { color:'#5fbf8f', fontSize:'16px' },
  dotClosed: { color:'#9a8a6e', fontSize:'16px' },
  historyTime: { fontSize:'13px' },
  historyDur: { color:'#9a8a6e', fontSize:'12px' },
  badgeOpen: { background:'#2d6a4f', color:'#5fbf8f', padding:'3px 10px', borderRadius:'20px', fontSize:'12px' },
  badgeClosed: { background:'#38301f', color:'#9a8a6e', padding:'3px 10px', borderRadius:'20px', fontSize:'12px' },
};
